import store from '@/store/store';
import { parseBoolean, parseString, parseNumber } from '@/utils/sanitaze.utils';
import { consoleError } from '@/utils/console.utils';
import { isArray, hasProp } from '@/utils/validate.utils';
import { MODES } from '@/app.config';

/**
 * @typedef {Object} Schedule
 * @property {String} id - Identificador de la programación
 * @property {Boolean} acs - Indica si la programación es de ACS
 * @property {Number} aqmode - Indica el modo de ionización
 * @property {Boolean} enabled - Indica si la programación está activada
 * @property {Array} devices - Dispositivos a los que afecta la programación
 * @property {Array} days - Dias en los que aplica la programación
 * @property {Number} hour - Hora de la programación
 * @property {Number} minutes - Minuto de la programación
 * @property {Number} mode - Modo de la programación
 * @property {String} name - Nombre de la programación
 * @property {Boolean} power - Indica si la programación es de encendido o apagado
 * @property {Boolean} power_acs - Indica si la programación es de encendido o apagado
 * @property {Number} setpoint - Temperatura seleccionada
 * @property {Number} speed_state - Velocidad del aire
 */

/**
 * Da formato a los datos de una programación
 *
 * @param {Object} scheduleData - Datos de una instalación del backend
 * @return {Schedule} - Datos formateados de una programación
 */
const sanitizeSchedule = scheduleData => {
  try {
    const schedule = {};

    schedule.acs = false;

    // Get schedule data and sanitize it
    if (hasProp(scheduleData, '_id')) {
      schedule.id = parseString(scheduleData._id);
    }
    if (hasProp(scheduleData, 'aqmode')) {
      schedule.aqmode = parseNumber(scheduleData.aqmode);
    }
    if (hasProp(scheduleData, 'enabled')) {
      schedule.enabled = parseBoolean(scheduleData.enabled);
    }
    if (hasProp(scheduleData, 'devices') && isArray(scheduleData.devices)) {
      schedule.devices = [];
      scheduleData.devices.forEach(device => {
        schedule.devices.push(parseString(device));
      });
    }
    if (hasProp(scheduleData, 'days') && isArray(scheduleData.days)) {
      schedule.days = [];
      scheduleData.days.forEach(day => {
        schedule.days.push(parseNumber(day));
      });
    }
    if (hasProp(scheduleData, 'hour')) {
      schedule.hour = parseNumber(scheduleData.hour);
    }
    if (hasProp(scheduleData, 'minutes')) {
      schedule.minutes = parseNumber(scheduleData.minutes);
    }
    if (hasProp(scheduleData, 'mode')) {
      schedule.mode = parseNumber(scheduleData.mode);
    }
    if (hasProp(scheduleData, 'name')) {
      schedule.name = parseString(scheduleData.name);
    }
    if (hasProp(scheduleData, 'power')) {
      schedule.power = parseString(scheduleData.power) === 'on';
    }
    if (hasProp(scheduleData, 'poweracs')) {
      schedule.acs = true;
      schedule.mode = MODES.HEATING;
      schedule.power = parseString(scheduleData.poweracs) === 'on';
      // schedule.power_acs = parseString(scheduleData.poweracs) === 'on';
    }
    if (hasProp(scheduleData, 'temp')) {
      schedule.setpoint = parseNumber(scheduleData.temp);
    }
    if (hasProp(scheduleData, 'tempwater')) {
      schedule.setpoint = parseNumber(scheduleData.tempwater);
    }
    if (hasProp(scheduleData, 'tempacs')) {
      schedule.setpoint = parseNumber(scheduleData.tempacs);
    }
    if (hasProp(scheduleData, 'vent')) {
      schedule.speed_state = parseNumber(scheduleData.vent);
    }

    return schedule;
  } catch (error) {
    consoleError('Error al parsear los datos de la programación');
    store.dispatch('globalModule/setError', 'invalidSchedule');
    return null;
  }
};

/**
 * Comprueba si los datos de una programación son válidos cuando se recibe por primera vez
 *
 * @param {Object} scheduleData - Datos de la programación del backend
 * @return {Schedule} - Datos formateados y validados de una programación

 */
const validateSchedule = scheduleData => {
  if (!hasProp(scheduleData, 'days') || !isArray(scheduleData.days)) {
    consoleError('Error al validar los datos de la programación');
    store.dispatch('globalModule/setError', 'invalidSchedule');
    return null;
  }

  const schedule = sanitizeSchedule(scheduleData);
  // schedule.days = scheduleData.days;
  // schedule.devices = scheduleData.devices;

  if (
    !schedule ||
    !schedule.id ||
    !schedule.name ||
    !hasProp(schedule, 'enabled') ||
    !hasProp(schedule, 'hour') ||
    !hasProp(schedule, 'minutes') ||
    !hasProp(schedule, 'power') ||
    !isArray(schedule.devices) ||
    !isArray(schedule.days)
  ) {
    consoleError('Error al validar los datos de la programación');
    store.dispatch('globalModule/setError', 'invalidSchedule');
    return null;
  }

  return schedule;
};

export { sanitizeSchedule, validateSchedule };
